// Messages Tab for Provider Dashboard - Direct Messaging with Clients
import React, { useState, useEffect } from 'react';
import { MessageCircle, ArrowLeft, Phone, Video, MoreVertical, User, Plus } from 'lucide-react';
import ConversationList from '../../messaging/ConversationList';
import MessageThread from '../../messaging/MessageThread';
import MessageInput from '../../messaging/MessageInput';
import { useMessaging } from '../../../contexts/MessagingContext';
import { useAuth } from '../../../contexts/AuthContext';

interface MessagesTabProps {
  // Optional: pre-select a client to start conversation
  initialClientId?: string;
  initialClientName?: string;
}

const MessagesTab: React.FC<MessagesTabProps> = ({
  initialClientId,
  initialClientName
}) => {
  const { user } = useAuth();
  const {
    conversations,
    activeConversation,
    messages,
    typingIndicator,
    loading,
    selectConversation,
    clearActiveConversation,
    sendMessage,
    createConversation,
    markAsRead,
    setTyping,
    archiveConversation
  } = useMessaging();

  const [showNewConversation, setShowNewConversation] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle initial client selection (e.g., from clicking "Message" on a client profile)
  useEffect(() => {
    if (initialClientId && initialClientName && user) {
      // Check if conversation exists, if not create one
      const existingConvo = conversations.find(
        (c) => c.clientId === initialClientId
      );
      if (existingConvo) {
        selectConversation(existingConvo.id);
      } else {
        // Could auto-create conversation here
        setShowNewConversation(true);
      }
    }
  }, [initialClientId, initialClientName, conversations, user]);

  // Handle sending message
  const handleSendMessage = async (content: string) => {
    try {
      await sendMessage(content);
    } catch (error) {
      console.error('Failed to send message:', error);
      // Could show error toast here
    }
  };

  // Handle back button in mobile view
  const handleBack = () => {
    clearActiveConversation();
  };

  // Render empty state when no conversation selected
  const renderEmptyState = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        backgroundColor: '#0f172a',
        padding: '40px'
      }}
    >
      <div
        style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: '#1e293b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '20px'
        }}
      >
        <MessageCircle size={40} color="#64748b" />
      </div>
      <h3
        style={{
          margin: '0 0 8px 0',
          fontSize: '18px',
          color: '#f8fafc',
          fontWeight: '600'
        }}
      >
        Select a conversation
      </h3>
      <p
        style={{
          margin: 0,
          fontSize: '14px',
          color: '#64748b',
          textAlign: 'center'
        }}
      >
        Choose a client from the list to start messaging
      </p>
    </div>
  );

  // Render conversation header
  const renderConversationHeader = () => {
    if (!activeConversation) return null;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          backgroundColor: '#1e293b',
          borderBottom: '1px solid #334155'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Back button for mobile */}
          {isMobileView && (
            <button
              onClick={handleBack}
              aria-label="Back to conversations"
              style={{
                background: 'none',
                border: 'none',
                padding: '8px',
                cursor: 'pointer',
                color: '#94a3b8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px'
              }}
            >
              <ArrowLeft size={20} />
            </button>
          )}

          {/* Avatar */}
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <User size={20} color="#fff" />
          </div>

          {/* Name and status */}
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: '15px',
                fontWeight: '600',
                color: '#f8fafc'
              }}
            >
              {activeConversation.clientName}
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: '12px',
                color: '#64748b'
              }}
            >
              Client
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            aria-label="Start phone call"
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              cursor: 'pointer',
              color: '#64748b',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#334155';
              e.currentTarget.style.color = '#f8fafc';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#64748b';
            }}
          >
            <Phone size={20} />
          </button>
          <button
            aria-label="Start video call"
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              cursor: 'pointer',
              color: '#64748b',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#334155';
              e.currentTarget.style.color = '#f8fafc';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#64748b';
            }}
          >
            <Video size={20} />
          </button>
          <button
            aria-label="More options"
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              cursor: 'pointer',
              color: '#64748b',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#334155';
              e.currentTarget.style.color = '#f8fafc';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#64748b';
            }}
          >
            <MoreVertical size={20} />
          </button>
        </div>
      </div>
    );
  };

  // Mobile view - show either list or conversation
  if (isMobileView) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {!activeConversation ? (
          <ConversationList
            conversations={conversations}
            activeConversationId={null}
            userRole="provider"
            onSelectConversation={selectConversation}
            onArchiveConversation={archiveConversation}
            loading={loading}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {renderConversationHeader()}
            <MessageThread
              messages={messages}
              currentUserId={user?.uid || ''}
              currentUserRole="provider"
              otherParticipantName={activeConversation.clientName}
              typingIndicator={typingIndicator}
              onMessagesViewed={markAsRead}
            />
            <MessageInput
              onSendMessage={handleSendMessage}
              onTyping={setTyping}
              placeholder={`Message ${activeConversation.clientName}...`}
            />
          </div>
        )}
      </div>
    );
  }

  // Desktop view - side by side
  return (
    <div
      style={{
        display: 'flex',
        height: '100%',
        backgroundColor: '#0f172a',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #334155'
      }}
    >
      {/* Conversation List - Left Side */}
      <div
        style={{
          width: '320px',
          flexShrink: 0,
          height: '100%'
        }}
      >
        <ConversationList
          conversations={conversations}
          activeConversationId={activeConversation?.id || null}
          userRole="provider"
          onSelectConversation={selectConversation}
          onArchiveConversation={archiveConversation}
          loading={loading}
        />
      </div>

      {/* Message Thread - Right Side */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}
      >
        {activeConversation ? (
          <>
            {renderConversationHeader()}
            <MessageThread
              messages={messages}
              currentUserId={user?.uid || ''}
              currentUserRole="provider"
              otherParticipantName={activeConversation.clientName}
              typingIndicator={typingIndicator}
              onMessagesViewed={markAsRead}
            />
            <MessageInput
              onSendMessage={handleSendMessage}
              onTyping={setTyping}
              placeholder={`Message ${activeConversation.clientName}...`}
            />
          </>
        ) : (
          renderEmptyState()
        )}
      </div>
    </div>
  );
};

export default MessagesTab;
